import { useEffect, useState } from 'react';
import { useDb } from './dbContext';
import type { LocalDb } from './localDb';
import { onTables } from './tableBus';

export interface QueryState<T> {
  data: T | undefined;
  error: unknown;
  loading: boolean;
}

/** Runs `fn` against the local DB, and again whenever one of `tables` changes. */
export function useQuery<T>(
  tables: readonly string[],
  fn: (db: LocalDb) => Promise<T>,
  deps: readonly unknown[] = [],
): QueryState<T> {
  const db = useDb();
  const [state, setState] = useState<QueryState<T>>({
    data: undefined,
    error: null,
    loading: true,
  });
  const [version, setVersion] = useState(0);
  const tablesKey = tables.join(',');

  useEffect(() => onTables(tables, () => setVersion((v) => v + 1)), [tablesKey]);

  useEffect(() => {
    let cancelled = false;
    fn(db).then(
      (data) => {
        if (!cancelled) setState({ data, error: null, loading: false });
      },
      (error: unknown) => {
        if (!cancelled) setState((s) => ({ data: s.data, error, loading: false }));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [db, version, tablesKey, ...deps]);

  return state;
}
